/**
 * Shared entity metadata for SEO entity pages (competitions, clubs, countries,
 * players, fixtures) and the build-time sitemap/prerender scripts.
 *
 * The prediction feed only stores a free-text `league` plus team names, so this
 * module is the single source of truth that maps those strings to structured
 * competition + country entities with schema.org types.
 */

export const SITE_URL = "https://statlign.com";
export const SITE_NAME = "Statlign";

export type CompetitionType = "league" | "cup" | "international";

export interface CompetitionMeta {
  slug: string;
  label: string;
  /** Substrings that may appear in the `league` column. */
  matchers: string[];
  countrySlug: string;
  countryLabel: string;
  competitionType: CompetitionType;
  /** schema.org type for JSON-LD. */
  schemaType: "SportsOrganization" | "SportsEvent";
}

export interface CountryMeta {
  slug: string;
  label: string;
}

export function slugify(value: string): string {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function titleCaseFromSlug(slug: string): string {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Known competitions, mapped to their country + schema type. */
export const COMPETITIONS: CompetitionMeta[] = [
  { slug: "premier-league", label: "Premier League", matchers: ["premier league", "epl", "english premier"], countrySlug: "england", countryLabel: "England", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "efl-championship", label: "EFL Championship", matchers: ["championship", "efl championship"], countrySlug: "england", countryLabel: "England", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "la-liga", label: "La Liga", matchers: ["la liga", "laliga", "primera division"], countrySlug: "spain", countryLabel: "Spain", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "la-liga-2", label: "La Liga 2", matchers: ["la liga 2", "segunda"], countrySlug: "spain", countryLabel: "Spain", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "serie-a", label: "Serie A", matchers: ["serie a"], countrySlug: "italy", countryLabel: "Italy", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "bundesliga", label: "Bundesliga", matchers: ["bundesliga"], countrySlug: "germany", countryLabel: "Germany", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "bundesliga-2", label: "Bundesliga 2", matchers: ["bundesliga 2", "2. bundesliga"], countrySlug: "germany", countryLabel: "Germany", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "ligue-1", label: "Ligue 1", matchers: ["ligue 1"], countrySlug: "france", countryLabel: "France", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "eredivisie", label: "Eredivisie", matchers: ["eredivisie"], countrySlug: "netherlands", countryLabel: "Netherlands", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "primeira-liga", label: "Primeira Liga", matchers: ["primeira liga", "liga portugal"], countrySlug: "portugal", countryLabel: "Portugal", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "jupiler-pro-league", label: "Jupiler Pro League", matchers: ["jupiler", "pro league", "belgian"], countrySlug: "belgium", countryLabel: "Belgium", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "brasileirao", label: "Brasileirão Série A", matchers: ["brasileir", "serie a brazil"], countrySlug: "brazil", countryLabel: "Brazil", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "mls", label: "Major League Soccer", matchers: ["mls", "major league soccer"], countrySlug: "usa", countryLabel: "USA", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "j-league", label: "J-League", matchers: ["j-league", "j league", "j1"], countrySlug: "japan", countryLabel: "Japan", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "danish-superliga", label: "Danish Superliga", matchers: ["superliga", "danish"], countrySlug: "denmark", countryLabel: "Denmark", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "swiss-super-league", label: "Swiss Super League", matchers: ["swiss super", "super league switzerland"], countrySlug: "switzerland", countryLabel: "Switzerland", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "super-league-greece", label: "Super League Greece", matchers: ["super league greece", "greek"], countrySlug: "greece", countryLabel: "Greece", competitionType: "league", schemaType: "SportsOrganization" },
  { slug: "scottish-premiership", label: "Scottish Premiership", matchers: ["scottish premiership", "scotland premier"], countrySlug: "scotland", countryLabel: "Scotland", competitionType: "league", schemaType: "SportsOrganization" },
  // Continental & international competitions.
  { slug: "champions-league", label: "UEFA Champions League", matchers: ["champions league", "ucl", "uefa champions"], countrySlug: "europe", countryLabel: "Europe", competitionType: "international", schemaType: "SportsEvent" },
  { slug: "europa-league", label: "UEFA Europa League", matchers: ["europa league", "uel"], countrySlug: "europe", countryLabel: "Europe", competitionType: "international", schemaType: "SportsEvent" },
  { slug: "conference-league", label: "UEFA Conference League", matchers: ["conference league", "uecl"], countrySlug: "europe", countryLabel: "Europe", competitionType: "international", schemaType: "SportsEvent" },
  { slug: "copa-libertadores", label: "Copa Libertadores", matchers: ["libertadores"], countrySlug: "south-america", countryLabel: "South America", competitionType: "international", schemaType: "SportsEvent" },
  { slug: "copa-sudamericana", label: "Copa Sudamericana", matchers: ["sudamericana"], countrySlug: "south-america", countryLabel: "South America", competitionType: "international", schemaType: "SportsEvent" },
  { slug: "world-cup", label: "FIFA World Cup 2026", matchers: ["world cup", "fifa world"], countrySlug: "world", countryLabel: "World", competitionType: "international", schemaType: "SportsEvent" },
];

export function findCompetition(slug: string): CompetitionMeta | undefined {
  return COMPETITIONS.find((c) => c.slug === slug);
}

/** Match a raw `league` string to a known competition. */
export function competitionForLeague(league: string): CompetitionMeta | undefined {
  const l = (league || "").toLowerCase();
  return COMPETITIONS.find((c) => c.matchers.some((m) => l.includes(m)));
}

export function listCountries(): CountryMeta[] {
  const map = new Map<string, string>();
  for (const c of COMPETITIONS) map.set(c.countrySlug, c.countryLabel);
  return [...map.entries()]
    .map(([slug, label]) => ({ slug, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function findCountry(slug: string): CountryMeta | undefined {
  return listCountries().find((c) => c.slug === slug);
}

export function competitionsForCountry(slug: string): CompetitionMeta[] {
  return COMPETITIONS.filter((c) => c.countrySlug === slug);
}

/** Loose matcher for a club slug (resilient to FC/CF suffixes). */
export function clubMatcherFromSlug(slug: string): string {
  const tokens = slug.split("-").filter((t) => t.length >= 3 && t !== "and");
  return tokens.sort((a, b) => b.length - a.length)[0] || slug;
}

/**
 * Keyword-rich fixture slug: `home-vs-away-YYYY-MM-DD`. The UTC match date
 * keeps the URL unique per matchday while staying human-readable and
 * SEO-friendly (no opaque hash).
 */
export function buildFixtureSlug(home: string, away: string, kickoff?: string | null): string {
  const date = (kickoff || "").slice(0, 10);
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "";
  return `${slugify(home)}-vs-${slugify(away)}${validDate ? `-${validDate}` : ""}`;
}

export interface ParsedFixtureSlug {
  homeSlug: string;
  awaySlug: string;
  date: string;
}

/**
 * Parse a fixture slug of the form `home-vs-away-YYYY-MM-DD` back into its
 * team slugs and match date. Returns null when the slug doesn't match.
 */
export function parseFixtureSlug(slug: string): ParsedFixtureSlug | null {
  const m = slug.match(/^(.+)-vs-(.+)-(\d{4}-\d{2}-\d{2})$/);
  if (!m) return null;
  const [, homeSlug, awaySlug, date] = m;
  if (!homeSlug || !awaySlug) return null;
  return { homeSlug, awaySlug, date: date! };
}
